import React, { useMemo, useState } from "react";
import {
  FaCalculator,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
} from "react-icons/fa";

import FuelSupplySummary from "../FieldApp/FuelSupplySummary";
import {
  enrichFuelingRecords,
  filterFuelingRecords,
  formatFuelIntervalRate,
  formatHaPerHour,
  formatTankFullLabel,
  FUEL_GROUP_BY,
  FUEL_INTERVAL_MODES,
  listFuelOperators,
} from "../FieldApp/fuelSupplyMetrics";

import "./OperationFuelSupplyPanel.css";

const DEFAULT_SETTINGS = {
  intervalMode: FUEL_INTERVAL_MODES.FILL_CLOSING,
  groupBy: FUEL_GROUP_BY.NONE,
  operatorId: "",
  dateFrom: "",
  dateTo: "",
};

function normalizeRecords(records) {
  return (records || []).map((record) => ({
    ...record,
    hour_meter: record.hour_meter != null ? record.hour_meter : record.hourMeter,
    equipmentName: record.equipmentName || record.machineIdent,
  }));
}

export default function OperationFuelSupplyPanel({
  records,
  totalAreaHa,
  totalHours,
  formatTimestamp,
  onOpenPhoto,
  t,
  defaultDateFrom,
  defaultDateTo,
}) {
  const [expanded, setExpanded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const normalizedRecords = useMemo(() => normalizeRecords(records), [records]);

  const operators = useMemo(
    () => listFuelOperators(normalizedRecords),
    [normalizedRecords]
  );

  const filteredRecords = useMemo(
    () =>
      filterFuelingRecords(normalizedRecords, {
        dateFrom: settings.dateFrom || defaultDateFrom,
        dateTo: settings.dateTo || defaultDateTo,
        operatorId: settings.operatorId,
      }),
    [normalizedRecords, settings, defaultDateFrom, defaultDateTo]
  );

  const enriched = useMemo(
    () =>
      enrichFuelingRecords(filteredRecords, {
        totalAreaHa,
        totalHours,
        intervalMode: settings.intervalMode,
        groupBy: settings.groupBy,
      }),
    [filteredRecords, totalAreaHa, totalHours, settings.intervalMode, settings.groupBy]
  );

  const intervalModeLabel =
    settings.intervalMode === FUEL_INTERVAL_MODES.FILL_CLOSING
      ? t("Fechamento por tanque cheio")
      : t("Abastecimentos consecutivos");

  const groupByLabel =
    settings.groupBy === FUEL_GROUP_BY.OPERATOR
      ? t("Por operador")
      : settings.groupBy === FUEL_GROUP_BY.WORK_DAY
        ? t("Por dia de trabalho")
        : t("Sem agrupamento");

  if (!normalizedRecords.length) return null;

  return (
    <div className="operation-fuel-supply-panel">
      <div className="operation-fuel-supply-panel-header">
        <div>
          <strong>{t("Abastecimentos do período")}</strong>
          <p className="operation-fuel-supply-panel-desc">
            {t(
              "Histórico permanente no servidor. Use o ícone de cálculo para escolher como gerar as médias."
            )}
          </p>
          <div className="operation-fuel-supply-chips">
            <span className="operation-fuel-supply-chip">
              {filteredRecords.length} {t("registros")}
            </span>
            <span className="operation-fuel-supply-chip">{intervalModeLabel}</span>
            {settings.groupBy !== FUEL_GROUP_BY.NONE ? (
              <span className="operation-fuel-supply-chip">{groupByLabel}</span>
            ) : null}
          </div>
        </div>
        <div className="operation-fuel-supply-panel-actions">
          <button
            type="button"
            className="btn btn-xs btn-default operation-fuel-supply-settings-btn"
            onClick={() => setSettingsOpen((open) => !open)}
            title={t("Opções de cálculo das médias")}
            aria-expanded={settingsOpen}
          >
            <FaCalculator aria-hidden /> {t("Médias")}
          </button>
          <button
            type="button"
            className="btn btn-xs btn-default operation-fuel-supply-toggle"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                <FaChevronUp aria-hidden /> {t("Recolher")}
              </>
            ) : (
              <>
                <FaChevronDown aria-hidden /> {t("Expandir")} ({filteredRecords.length})
              </>
            )}
          </button>
        </div>
      </div>

      {settingsOpen ? (
        <div className="operation-fuel-supply-settings">
          <div className="operation-fuel-supply-settings-head">
            <strong>{t("Como calcular as médias")}</strong>
            <button
              type="button"
              className="operation-fuel-supply-settings-close"
              onClick={() => setSettingsOpen(false)}
              aria-label={t("Fechar")}
            >
              <FaTimes />
            </button>
          </div>

          <div className="operation-fuel-supply-settings-grid">
            <label className="operation-fuel-supply-settings-field">
              <span>{t("Referência do intervalo")}</span>
              <select
                className="form-control input-sm"
                value={settings.intervalMode}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    intervalMode: event.target.value,
                  }))
                }
              >
                <option value={FUEL_INTERVAL_MODES.FILL_CLOSING}>
                  {t("Fechamento por tanque cheio")}
                </option>
                <option value={FUEL_INTERVAL_MODES.CONSECUTIVE}>
                  {t("Abastecimentos consecutivos")}
                </option>
              </select>
              <small className="text-muted">
                {settings.intervalMode === FUEL_INTERVAL_MODES.FILL_CLOSING
                  ? t(
                      "Só fecha o intervalo quando o operador informa tanque cheio. Abastecimentos parciais somam litros até o próximo fechamento."
                    )
                  : t(
                      "Cada abastecimento compara com o anterior na mesma máquina."
                    )}
              </small>
            </label>

            <label className="operation-fuel-supply-settings-field">
              <span>{t("Agrupar médias")}</span>
              <select
                className="form-control input-sm"
                value={settings.groupBy}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    groupBy: event.target.value,
                  }))
                }
              >
                <option value={FUEL_GROUP_BY.NONE}>{t("Sem agrupamento")}</option>
                <option value={FUEL_GROUP_BY.OPERATOR}>{t("Por operador")}</option>
                <option value={FUEL_GROUP_BY.WORK_DAY}>
                  {t("Por dia de trabalho")}
                </option>
              </select>
            </label>

            <label className="operation-fuel-supply-settings-field">
              <span>{t("Operador")}</span>
              <select
                className="form-control input-sm"
                value={settings.operatorId}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    operatorId: event.target.value,
                  }))
                }
              >
                <option value="">{t("Todos os operadores")}</option>
                {operators.map((operator) => (
                  <option key={operator.id} value={operator.id}>
                    {operator.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="operation-fuel-supply-settings-field">
              <span>{t("Data inicial")}</span>
              <input
                type="date"
                className="form-control input-sm"
                value={settings.dateFrom}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    dateFrom: event.target.value,
                  }))
                }
              />
            </label>

            <label className="operation-fuel-supply-settings-field">
              <span>{t("Data final")}</span>
              <input
                type="date"
                className="form-control input-sm"
                value={settings.dateTo}
                onChange={(event) =>
                  setSettings((prev) => ({
                    ...prev,
                    dateTo: event.target.value,
                  }))
                }
              />
            </label>
          </div>
        </div>
      ) : null}

      <FuelSupplySummary
        summary={enriched.summary}
        groupedSummaries={enriched.groupedSummaries}
        t={t}
        showHaNote={enriched.totalAreaHa > 0}
        compact={!expanded}
      />

      {expanded ? (
        <div className="operation-fuel-supply-table-scroll field-app-fueling-table-wrap table-responsive">
          <table className="table table-striped jambo_table operation-fuel-records-table">
            <thead>
              <tr>
                <th>{t("Data")}</th>
                <th>{t("Máquina")}</th>
                <th>{t("Operador")}</th>
                <th>{t("Litros")}</th>
                <th>{t("Horímetro")}</th>
                <th>{t("Tanque")}</th>
                <th>{t("L/h intervalo")}</th>
                <th>{t("L/ha intervalo")}</th>
                <th>{t("ha/h intervalo")}</th>
                <th>{t("Foto")}</th>
              </tr>
            </thead>
            <tbody>
              {enriched.rows.map((record) => (
                <tr key={record.uuid || record.timestamp}>
                  <td>{formatTimestamp(record.timestamp)}</td>
                  <td>{record.machineIdent || record.equipmentName || "—"}</td>
                  <td>{record.operatorName || "—"}</td>
                  <td>{record.liters}</td>
                  <td>
                    {record.hourMeter != null && record.hourMeter !== ""
                      ? record.hourMeter
                      : record.hour_meter != null && record.hour_meter !== ""
                        ? record.hour_meter
                        : "—"}
                  </td>
                  <td>
                    <span
                      className={`operation-fuel-tank-badge operation-fuel-tank-badge--${
                        record.tankFull === true
                          ? "full"
                          : record.tankFull === false
                            ? "partial"
                            : "unknown"
                      }`}
                    >
                      {formatTankFullLabel(record, t)}
                    </span>
                  </td>
                  <td className="fuel-metric-cell">
                    {record.hasFuelInterval
                      ? formatFuelIntervalRate(
                          record.intervalLitersPerHour,
                          t("L/h")
                        )
                      : settings.intervalMode === FUEL_INTERVAL_MODES.FILL_CLOSING &&
                          record.tankFull !== true
                        ? t("Parcial")
                        : "—"}
                  </td>
                  <td className="fuel-metric-cell">
                    {record.hasFuelInterval
                      ? formatFuelIntervalRate(
                          record.intervalLitersPerHa,
                          t("L/ha")
                        )
                      : "—"}
                  </td>
                  <td className="fuel-metric-cell">
                    {record.hasFuelInterval
                      ? formatHaPerHour(record.intervalHaPerHour)
                      : "—"}
                  </td>
                  <td>
                    {record.hasPanelPhoto ? (
                      <button
                        type="button"
                        className="operation-field-event-photo-btn"
                        onClick={() => onOpenPhoto(record.panelPhotoUrl)}
                      >
                        📷 {t("Foto")}
                      </button>
                    ) : (
                      <span className="text-muted">{t("sem foto")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
